/**
 * StepConfigForm — per-step (node) configuration surface for the process
 * editor. Edits a StepConfig: persona mode, task prompt, optional
 * system-prompt override, tools selection tree, and max_iterations.
 * `context_requirements` is API-only in v1 and not surfaced here.
 */

import type { StepConfig, SystemPromptOverride, ToolSelection } from '@localagent/sdk';
import { ToolSelectionTree } from './ToolSelectionTree';

type PersonaMode = 'parent' | 'auto' | 'named';

interface StepConfigFormProps {
  config: StepConfig;
  onChange: (config: StepConfig) => void;
  disabled: boolean;
  /** Validation issues scoped to this node (best-effort path match). */
  issues: string[];
  /** Account persona names (user's own). */
  personaNames: string[];
  /** System persona names (global). */
  systemPersonaNames: string[];
  /** Unique prefix for input test ids, e.g. "step-1-0". */
  testIdPrefix: string;
  /** Forwarded to ToolSelectionTree for prop-driven Storybook stories. */
  catalogTools?: Array<{ name: string; description: string; mcp_id: string }>;
}

const inputClass =
  'w-full rounded-md bg-muted px-2 py-1 text-caption focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50';

function getPersonaMode(config: StepConfig): PersonaMode {
  if (config.persona_mode === 'auto') return 'auto';
  if (config.persona_mode === 'named' || config.persona_name) return 'named';
  return 'parent';
}

export function StepConfigForm({
  config,
  onChange,
  disabled,
  issues,
  personaNames,
  systemPersonaNames,
  testIdPrefix,
  catalogTools,
}: StepConfigFormProps) {
  const hasSystemPrompt = config.system_prompt != null;
  const personaMode = getPersonaMode(config);

  const setPersonaMode = (mode: PersonaMode) => {
    const next = { ...config };
    if (mode === 'parent') {
      next.persona_mode = 'parent';
      delete next.persona_name;
    } else if (mode === 'auto') {
      next.persona_mode = 'auto';
      delete next.persona_name;
    } else {
      next.persona_mode = 'named';
    }
    onChange(next);
  };

  const setPersonaName = (name: string) => {
    const next = { ...config, persona_mode: 'named' as const };
    if (name) next.persona_name = name;
    else delete next.persona_name;
    onChange(next);
  };

  const setSystemPrompt = (override: SystemPromptOverride | undefined) => {
    const next = { ...config };
    if (override) next.system_prompt = override;
    else delete next.system_prompt;
    onChange(next);
  };

  const setTools = (selection: ToolSelection | undefined) => {
    const next = { ...config };
    if (selection && (selection.include?.length || selection.exclude?.length)) {
      next.tools = selection;
    } else {
      delete next.tools;
    }
    onChange(next);
  };

  const filteredAccountPersonas = personaNames.filter((n) => !n.startsWith('_system_'));
  const filteredSystemPersonas = systemPersonaNames.filter((n) => !n.startsWith('_system_'));

  return (
    <div className="space-y-3">
      {issues.length > 0 && (
        <ul className="rounded bg-destructive/10 p-2 text-micro text-destructive">
          {issues.map((msg, i) => (
            <li key={i}>{msg}</li>
          ))}
        </ul>
      )}

      {/* Persona section */}
      <div>
        <label className="mb-1.5 block text-caption font-medium">Persona</label>
        <div className="space-y-1.5">
          <label className="flex cursor-pointer items-center gap-2 text-caption">
            <input
              type="radio"
              name={`${testIdPrefix}-persona-mode`}
              data-testid={`${testIdPrefix}-persona-mode-parent`}
              checked={personaMode === 'parent'}
              onChange={() => setPersonaMode('parent')}
              disabled={disabled}
              className="h-3 w-3"
            />
            Use parent persona
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-caption">
            <input
              type="radio"
              name={`${testIdPrefix}-persona-mode`}
              data-testid={`${testIdPrefix}-persona-mode-auto`}
              checked={personaMode === 'auto'}
              onChange={() => setPersonaMode('auto')}
              disabled={disabled}
              className="h-3 w-3"
            />
            Auto persona
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-caption">
            <input
              type="radio"
              name={`${testIdPrefix}-persona-mode`}
              data-testid={`${testIdPrefix}-persona-mode-named`}
              checked={personaMode === 'named'}
              onChange={() => setPersonaMode('named')}
              disabled={disabled}
              className="h-3 w-3"
            />
            Specific persona
          </label>
          {personaMode === 'named' && (
            <select
              data-testid={`${testIdPrefix}-persona`}
              value={config.persona_name ?? ''}
              onChange={(e) => setPersonaName(e.target.value)}
              disabled={disabled}
              className={`${inputClass} ml-5`}
            >
              <option value="">— Select persona —</option>
              {filteredAccountPersonas.length > 0 && (
                <optgroup label="Account Personas">
                  {filteredAccountPersonas.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </optgroup>
              )}
              {filteredSystemPersonas.length > 0 && (
                <optgroup label="Global Personas">
                  {filteredSystemPersonas.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          )}
        </div>
      </div>

      <div>
        <label className="mb-1 block text-caption font-medium">Max iterations</label>
        <input
          type="number"
          min={1}
          max={2500}
          data-testid={`${testIdPrefix}-max-iterations`}
          value={config.max_iterations ?? ''}
          onChange={(e) => {
            const next = { ...config };
            const parsed = parseInt(e.target.value, 10);
            if (Number.isInteger(parsed)) next.max_iterations = parsed;
            else delete next.max_iterations;
            onChange(next);
          }}
          disabled={disabled}
          placeholder="default"
          className={inputClass}
        />
      </div>

      <div>
        <label className="mb-1 block text-caption font-medium">
          Task prompt <span className="text-destructive">*</span>
        </label>
        <textarea
          data-testid={`${testIdPrefix}-task-prompt`}
          value={config.task_prompt}
          onChange={(e) => onChange({ ...config, task_prompt: e.target.value })}
          rows={2}
          disabled={disabled}
          placeholder={'Supports {{user_message}} templating'}
          className={`${inputClass} font-mono`}
        />
      </div>

      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          id={`${testIdPrefix}-sp-enabled`}
          data-testid={`${testIdPrefix}-system-prompt-enabled`}
          checked={hasSystemPrompt}
          disabled={disabled}
          onChange={(e) =>
            setSystemPrompt(e.target.checked ? { mode: 'append', content: '' } : undefined)
          }
          className="mt-0.5 h-3.5 w-3.5 rounded border-input"
        />
        <div className="flex-1">
          <label htmlFor={`${testIdPrefix}-sp-enabled`} className="cursor-pointer text-caption font-medium">
            System prompt override
          </label>
          {hasSystemPrompt && (
            <div className="mt-1 space-y-1">
              <select
                data-testid={`${testIdPrefix}-system-prompt-mode`}
                value={config.system_prompt!.mode}
                onChange={(e) =>
                  setSystemPrompt({
                    mode: e.target.value as SystemPromptOverride['mode'],
                    content: config.system_prompt!.content,
                  })
                }
                disabled={disabled}
                className={inputClass}
              >
                <option value="append">Append to persona prompt</option>
                <option value="replace">Replace persona prompt</option>
              </select>
              <textarea
                data-testid={`${testIdPrefix}-system-prompt-content`}
                value={config.system_prompt!.content}
                onChange={(e) =>
                  setSystemPrompt({ mode: config.system_prompt!.mode, content: e.target.value })
                }
                rows={2}
                disabled={disabled}
                className={`${inputClass} font-mono`}
              />
            </div>
          )}
        </div>
      </div>

      <div>
        <label className="mb-1 block text-caption font-medium">Tools</label>
        <ToolSelectionTree
          value={config.tools}
          onChange={setTools}
          disabled={disabled}
          mode="step"
          testIdPrefix={`${testIdPrefix}-tools`}
          catalogTools={catalogTools}
        />
      </div>
    </div>
  );
}
